/**
 * Brief 3.6 Item 4 — Daily briefing v2 tile.
 *
 * Replaces the LLM-composed text-blob "Today's standup" with a
 * structured 3-section briefing: what changed yesterday, what needs
 * attention today, what Nerea wants you to know.
 */
import { useEffect, useState } from "react";
import {
  Sparkles, AlertTriangle, Clock, Flag, ChevronDown, ChevronRight, RefreshCw,
} from "lucide-react";
import api from "../../lib/api";

const TONE_PILL = {
  red: "bg-rose-500/10 text-rose-300 border-rose-400/30",
  amber: "bg-amber-400/10 text-amber-200 border-amber-400/25",
  green: "bg-emerald-400/10 text-emerald-200 border-emerald-400/25",
};

const REASON_LABEL = {
  incorrect: "Incorrect",
  not_helpful: "Not helpful",
  needs_human: "Needs human",
};

export default function DailyBriefingTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadTick, setReloadTick] = useState(0);
  const [openSection, setOpenSection] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError("");
      try {
        const { data: body } = await api.get("/admin/ops/daily-briefing");
        if (!cancelled) setData(body);
      } catch (e) {
        if (!cancelled) setError("Couldn't load today's briefing.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadTick]);

  if (loading) {
    return (
      <div data-testid="daily-briefing-loading" className="rounded-2xl border border-[#00E5FF]/15 bg-[#00E5FF]/[0.04] p-5 text-[12.5px] text-zinc-500">
        Loading today's briefing…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div data-testid="daily-briefing-error" className="rounded-2xl border border-rose-400/20 bg-rose-400/[0.04] p-5 text-[12.5px] text-rose-300">
        {error || "Briefing unavailable."}
      </div>
    );
  }

  const h = data.headline_counts || {};
  const sections = data.sections || [];

  return (
    <div
      data-testid="daily-briefing-tile"
      className="rounded-2xl border border-[#00E5FF]/15 bg-[#00E5FF]/[0.04] p-5"
    >
      {/* Header + headline counts */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-zinc-400">
            <Sparkles className="h-3 w-3 text-[#00E5FF]" /> Today's briefing
          </div>
          <h2 className="mt-1 text-[18px] font-semibold tracking-tight text-white">
            {pickHeadline(h)}
          </h2>
        </div>
        <button
          type="button"
          onClick={() => setReloadTick((n) => n + 1)}
          data-testid="daily-briefing-refresh"
          className="inline-flex items-center gap-1 text-[11.5px] text-zinc-400 hover:text-[#00E5FF]"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        <Pill testid="briefing-pill-decisions"
              label={`${h.decisions_24h ?? 0} decisions in 24h`} tone="green" />
        <Pill testid="briefing-pill-sla-breached"
              label={`${h.sla_breached ?? 0} SLA breached`}
              tone={(h.sla_breached || 0) > 0 ? "red" : "green"} />
        <Pill testid="briefing-pill-sla-at-risk"
              label={`${h.sla_at_risk ?? 0} SLA at risk`}
              tone={(h.sla_at_risk || 0) > 0 ? "amber" : "green"} />
        <Pill testid="briefing-pill-pending"
              label={`${h.pending_actions ?? 0} actions pending`}
              tone={(h.pending_actions || 0) > 0 ? "amber" : "green"} />
        <Pill testid="briefing-pill-flags"
              label={`${h.merchant_flags_24h ?? 0} merchant flags`}
              tone={(h.merchant_flags_24h || 0) > 0 ? "amber" : "green"} />
        <Pill testid="briefing-pill-abstain"
              label={`${Math.round((h.abstain_rate_24h ?? 0) * 100)}% abstain`}
              tone={(h.abstain_rate_24h ?? 0) > 0.4 ? "amber" : "green"} />
      </div>

      <div className="mt-5 space-y-2">
        {sections.map((s, i) => (
          <Section
            key={s.title}
            section={s}
            open={openSection === i}
            onToggle={() => setOpenSection(openSection === i ? -1 : i)}
            testid={`briefing-section-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

function pickHeadline(h) {
  if ((h.sla_breached ?? 0) > 0) return `${h.sla_breached} SLA breach${h.sla_breached === 1 ? "" : "es"} need a decision today.`;
  if ((h.merchant_flags_24h ?? 0) > 0) return `${h.merchant_flags_24h} merchant${h.merchant_flags_24h === 1 ? "" : "s"} asked for a human yesterday.`;
  if ((h.pending_actions ?? 0) > 0) return `${h.pending_actions} pending action${h.pending_actions === 1 ? "" : "s"} waiting on you.`;
  if ((h.sla_at_risk ?? 0) > 0) return `${h.sla_at_risk} merchant${h.sla_at_risk === 1 ? "" : "s"} at SLA risk — keep an eye on them.`;
  if ((h.decisions_24h ?? 0) === 0) return "Quiet morning — nothing flagged.";
  return "Standing by — see the breakdown below.";
}

function Pill({ label, tone, testid }) {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] ${TONE_PILL[tone] || TONE_PILL.green}`}
    >
      {label}
    </span>
  );
}

function Section({ section, open, onToggle, testid }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02]" data-testid={testid}>
      <button
        type="button"
        onClick={onToggle}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left"
      >
        <div className="flex items-center gap-2 text-[13px] text-white">
          {open ? <ChevronDown className="h-3.5 w-3.5 text-zinc-400" /> : <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />}
          {section.title}
        </div>
        <span className="text-[11px] text-zinc-500">{summaryLine(section)}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-2.5 text-[12.5px] text-zinc-300" data-testid={`${testid}-body`}>
          {renderSectionBody(section)}
        </div>
      )}
    </div>
  );
}

function summaryLine(s) {
  if (s.title === "What changed yesterday") {
    return `${s.total_decisions_24h ?? 0} decisions`;
  }
  if (s.title === "What needs your attention today") {
    const c = s.counts || {};
    return `${c.sla_breached ?? 0}🚨 · ${c.sla_at_risk ?? 0}⏰ · ${c.pending_actions ?? 0}🔍`;
  }
  if (s.title === "What Nerea wants you to know") {
    return `${s.merchant_flags?.length ?? 0} merchant + ${s.auto_flags?.length ?? 0} auto`;
  }
  return "";
}

function renderSectionBody(s) {
  if (s.title === "What changed yesterday") {
    return (
      <>
        {(s.individual_decisions || []).length === 0 && (s.bulk_actions || []).length === 0 && (
          <div className="text-zinc-500">No decisions in the last 24h.</div>
        )}
        {(s.bulk_actions || []).map((b) => (
          <div key={b.bulk_action_id} className="rounded-md border border-amber-400/20 bg-amber-400/[0.04] p-2.5">
            <div className="text-amber-200 text-[12.5px]">
              <strong>{b.actor_email || "unknown"}</strong> ran a {b.decision || "bulk"} on{" "}
              <strong>{b.merchant_ids?.length ?? 0}</strong> merchants at{" "}
              {fmtTime(b.timestamp)}.
            </div>
            {b.reason && <div className="mt-1 text-zinc-400 text-[11.5px]">"{b.reason}"</div>}
          </div>
        ))}
        {(s.individual_decisions || []).map((d) => (
          <div key={`${d.merchant_id}-${d.timestamp}`} className="flex items-start gap-2 text-[12.5px]">
            <Clock className="h-3 w-3 text-zinc-500 mt-0.5 shrink-0" />
            <div>
              <span className="text-zinc-300">{d.summary}</span>
              <span className="text-zinc-500"> · {fmtTime(d.timestamp)}{d.actor ? ` · by ${d.actor}` : ""}</span>
            </div>
          </div>
        ))}
      </>
    );
  }
  if (s.title === "What needs your attention today") {
    return (
      <>
        {(s.sla_breached || []).length === 0
          && (s.sla_at_risk || []).length === 0
          && (s.pending_actions || []).length === 0 && (
          <div className="text-zinc-500">Nothing on the at-risk list. Standing by.</div>
        )}
        {(s.sla_breached || []).map((m) => (
          <div key={`b-${m.merchant_id}`} className="rounded-md border border-rose-400/30 bg-rose-400/[0.06] p-2.5">
            <div className="text-rose-200 text-[12.5px] flex items-center gap-1.5">
              <AlertTriangle className="h-3 w-3" />
              <strong>{m.legal_name}</strong> — overdue by {(m.sla?.hours_overdue ?? 0).toFixed(1)}h
            </div>
          </div>
        ))}
        {(s.sla_at_risk || []).map((m) => (
          <div key={`r-${m.merchant_id}`} className="rounded-md border border-amber-400/20 bg-amber-400/[0.04] p-2.5">
            <div className="text-amber-200 text-[12.5px]">
              <strong>{m.legal_name}</strong> — {(m.sla?.hours_remaining ?? 0).toFixed(1)}h remaining
            </div>
          </div>
        ))}
        {(s.pending_actions || []).map((a) => (
          <div key={a.action_id || a._id} className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5">
            <div className="text-zinc-200 text-[12.5px]">
              {a.action_type || "Pending action"} — {a.merchant_id || ""}
            </div>
          </div>
        ))}
      </>
    );
  }
  // Section 3
  return (
    <>
      {(s.merchant_flags || []).length === 0 && (s.auto_flags || []).length === 0 && (
        <div className="text-zinc-500">No flagged content in the last 24h.</div>
      )}
      {(s.merchant_flags || []).map((f) => (
        <div key={f.flag_id} className="rounded-md border border-rose-400/20 bg-rose-400/[0.04] p-2.5">
          <div className="text-rose-200 text-[12.5px] flex items-center gap-1.5">
            <Flag className="h-3 w-3" />
            Merchant marked Nerea's answer as{" "}
            <span className="font-medium">{REASON_LABEL[f.reason] || f.reason}</span>
          </div>
          {f.note && <div className="mt-1 text-zinc-400 text-[11.5px]">"{f.note}"</div>}
          <div className="mt-1 text-zinc-500 text-[10.5px]">{f.merchant_id} · {fmtTime(f.created_at)}</div>
        </div>
      ))}
      {(s.auto_flags || []).map((f) => (
        <div key={f.flag_id} className="rounded-md border border-amber-400/20 bg-amber-400/[0.04] p-2.5">
          <div className="text-amber-200 text-[12.5px]">Auto-flagged: {f.note || f.reason}</div>
          <div className="mt-1 text-zinc-500 text-[10.5px]">{fmtTime(f.created_at)}</div>
        </div>
      ))}
      <div className="text-zinc-500 text-[11.5px] pt-1">
        24h Nerea volume: {s.usage_24h?.total_turns ?? 0} turns,{" "}
        {Math.round((s.usage_24h?.abstain_rate ?? 0) * 100)}% abstained.
      </div>
    </>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}
